import { Dispatch, SetStateAction, useState } from 'react'

import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material'
import { GameType } from 'constants/enums/GameTypeEnum'
import { brainstormText } from 'content/brainstormText'

import AgreedTermsCheckbox from 'components/AgreedTermsCheckbox/AgreedTermsCheckbox'
import { RichTextField } from 'components/Form'
import { GeneratedExampleDropdown } from 'components/GeneratedExampleDropdown/GeneratedExampleDropdown'
import IdeaFlameMotivator, {
	useRichTextToIdeas,
} from 'components/IdeaFlameMotivator/IdeaFlameMotivator'

import { commonStyles } from 'styles/common.styles'

import { styles } from './IdeationSection.styles'

export const TEST_ID = 'battle-ships-ideation-section'

export type Props = {
	authorIdeation: string[]
	cautionText?: string
	currentResponses: string[][]
	gameId?: string
	gameTypeEnum: GameType
	handleChange: (i: number) => (val: string) => void
	handleSubmitIdeas: () => void
	isLoading: boolean
	setTermsAgreed: Dispatch<SetStateAction<boolean>>
	submitIdeasDisabled: () => boolean
	termsAgreed: boolean
	viewSubmittedIdeas: () => void
}

const olStyle = {
	paddingLeft: '2rem',
}

const buildResponseItem = (solution: string[]) => {
	return (
		<Box sx={styles.exampleIdeasBox}>
			<ol data-testid="gen-ai-response-list">
				{solution.map((el: string, i: number) => (
					<Typography
						fontSize={16}
						component="li"
						key={`ai-solution-${i}`}
						variant="body1"
						style={styles.listItem}
					>
						{el}
					</Typography>
				))}
			</ol>
		</Box>
	)
}

const IdeationSection = ({
	gameTypeEnum,
	authorIdeation,
	isLoading,
	handleChange,
	submitIdeasDisabled,
	handleSubmitIdeas,
	viewSubmittedIdeas,
	currentResponses,
	cautionText,
	setTermsAgreed,
	termsAgreed,
	gameId,
}: Props) => {
	const richTextToIdeasFns = [
		useRichTextToIdeas(),
		useRichTextToIdeas(),
		useRichTextToIdeas(),
	]
	const [showAIExampleIdeas, setShowAIExampleIdeas] = useState([
		false,
		false,
		false,
	])

	const showSingleDropdown = (index: number) => {
		setShowAIExampleIdeas((prev) => {
			const newState = [...prev]
			newState[index] = !newState[index]
			return newState
		})
	}

	return (
		<>
			<Typography
				variant={'overline'}
				sx={{ ...commonStyles.header, ...styles.header }}
				data-testid={`${TEST_ID}__header`}
			>
				3. IDEATION
			</Typography>
			<Box display={'flex'} sx={styles.proseMirror}>
				<Box marginRight={'2rem'} sx={styles.sideBar} />
				<Box sx={styles.mainContainer}>
					<Box sx={styles.threeColumnLayout}>
						{currentResponses.map(
							(
								_d, // we need the item '_d' here even if it is not used.
								i
							) => (
								<Box
									key={`${TEST_ID}__prompt-box_${i}`}
									data-testid={`${TEST_ID}__prompt-box_${i}`}
									sx={styles.columnBox}
								>
									<ol start={i + 1} style={olStyle}>
										<Typography sx={commonStyles.header}>
											<li>
												{
													brainstormText.battleShips
														.ideationTitles[i]
												}
											</li>
										</Typography>
									</ol>
									<Grid
										item
										sx={styles.gridItem}
										data-testid={`${TEST_ID}__ideation`}
									>
										<RichTextField
											onChange={handleChange(i)}
											placeholderText={
												brainstormText.battleShips
													.ideationPlaceholders[i]
											}
											value={authorIdeation[i] ?? ''}
											sx={styles.richTextField}
											iconColor="#45657B"
											editorId={`editor-${i}`}
										/>
									</Grid>
									<Box>
										<IdeaFlameMotivator
											ideaCount={richTextToIdeasFns[i](
												authorIdeation[i]
											)}
											variant="BATTLE_SHIPS"
										/>
									</Box>
								</Box>
							)
						)}
					</Box>
					<Box>
						<Typography sx={styles.unsureText}>
							Unsure of how to apply the facilitation technique to
							your innovation topic?
						</Typography>
						<Box sx={styles.dropdownContainer}>
							{currentResponses.map((_s, i) => (
								<Box
									key={`${TEST_ID}__generated-example_${i}`}
									data-testid={`${TEST_ID}__dropdown-box_${i}`}
									sx={styles.columnBox}
								>
									<GeneratedExampleDropdown
										isLoading={isLoading}
										gameTypeEnum={gameTypeEnum}
										headerText={''}
										currentResponses={currentResponses[i]}
										customResponse={buildResponseItem(
											currentResponses[i]
										)}
										showAIExampleIdea={
											showAIExampleIdeas[i]
										}
										setShowAIExampleIdea={() =>
											showSingleDropdown(i)
										}
									/>
								</Box>
							))}
						</Box>
					</Box>
					<AgreedTermsCheckbox
						setTermsAgreed={setTermsAgreed}
						termsAgreed={termsAgreed}
						gameId={gameId}
					/>
					<Box sx={styles.buttonContainer}>
						<Button
							color="secondary"
							variant="contained"
							data-testid={`${TEST_ID}__submit-idea-button`}
							onClick={handleSubmitIdeas}
							disabled={submitIdeasDisabled() || isLoading}
						>
							{isLoading ? (
								<CircularProgress
									color="secondary"
									sx={commonStyles.circularProgress}
								/>
							) : (
								<Typography>SUBMIT IDEAS</Typography>
							)}
						</Button>
						<Button
							disabled={isLoading}
							variant="text"
							color="secondary"
							data-testid={`${TEST_ID}__view-submit-ideas-button`}
							onClick={viewSubmittedIdeas}
						>
							{isLoading ? (
								<CircularProgress
									color="secondary"
									sx={commonStyles.circularProgress}
								/>
							) : (
								<Typography>
									VIEW ALL SUBMITTED IDEAS
								</Typography>
							)}
						</Button>
					</Box>
					<Typography
						sx={{
							...styles.cautionText,
							...commonStyles.header,
						}}
					>
						{cautionText}
					</Typography>
				</Box>
			</Box>
		</>
	)
}

export default IdeationSection
